import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import { AuthLayout } from '@/components';
import { AuthFormField, authFormFieldControlClassName, authPrimarySubmitClassName, authSecondaryOutlineClassName } from '@/components/auth/AuthFormField';
import { cn } from '@/lib/utils';
import { forgotPassword } from '@/apis/auth.js';
import { resolveAuthLoginError } from '@/lib/auth/resolveAuthLoginError';
import { useSsoLoginRequired } from '@/hooks/queries/useSsoLoginRequired';

const ForgotPassword = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const [email, setEmail] = useState('');
  const ssoOnly = useSsoLoginRequired(email);
  const [loading, setLoading] = useState(false);
  const [emailError, setEmailError] = useState('');

  // Email validation function
  const isEmailValid = (email) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

  const handleEmailChange = (e) => {
    const value = e.target.value;
    setEmail(value);
    if (value && !isEmailValid(value)) {
      setEmailError(t('forget_password.email_invalid'));
    } else {
      setEmailError('');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!email) {
      setEmailError(t('forget_password.email_required'));
      return;
    }
    if (!isEmailValid(email)) {
      setEmailError(t('forget_password.email_invalid'));
      return;
    }
    
    if (ssoOnly) {
      toast.error(resolveAuthLoginError('sso_required', t));
      return;
    }

    try {
      setLoading(true);
      await forgotPassword(email);
      toast.success(t('forget_password.success_message'));
    } catch (error) {
      console.error('Forgot password error:', error);
      const apiMessage = error?.data?.message || error?.response?.data?.message || error?.message;
      const errorMessage =
        apiMessage === 'sso_required'
          ? resolveAuthLoginError('sso_required', t)
          : apiMessage || t('forget_password.error_message');
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthLayout title={t('forget_password.title')} subtitle={t('forget_password.subtitle')}>
      <form onSubmit={handleSubmit} className="space-y-4">
        <AuthFormField
          id="email"
          label={t('forget_password.email')}
          required
          invalid={!!emailError}
          hint={emailError ? <p className="text-sm text-red-500">{emailError}</p> : null}
        >
          <Input
            id="email"
            type="email"
            placeholder={t('login.email_placeholder')}
            value={email}
            onChange={handleEmailChange}
            autoComplete="email"
            className={authFormFieldControlClassName}
          />
        </AuthFormField>

        <div className="space-y-2">
          <Button
            type="submit"
            disabled={loading}
            className={authPrimarySubmitClassName}
          >
            {loading ? t('common.loading') : t('forget_password.send_reset_link')}
          </Button>

          <Button
            type="button"
            variant="outline"
            className={cn(authSecondaryOutlineClassName, 'bg-transparent hover:bg-gray-50')}
            onClick={() => navigate('/login')}
          >
            {t('forget_password.back_to_login')}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
};

export default ForgotPassword;
